import { Component, OnDestroy, OnInit } from '@angular/core';
import { HeaderService } from '@app/service/header/header.service';
import { CheckoutState, StateControlParameters, StateService } from '@app/service/state/state.service';
import { ActivatedRoute, Router } from '@angular/router';
import { QueryParamsService } from '@app/service/query-params/query-params.service';
import { AccountCheckReportResponse, AccountReportStatus, AisAccountService, AisProvider, PayloadGetFinsharkReport, PayloadGetTinkReport } from '@app/service/ais-account/ais-account.service';
import { RouteComponent } from '@app/route/route.component';
import { QueryParams } from '@app/service/query-params/query-params.class';
import { LoggingService } from '@app/service/logging-service/logging.service';
import { routeNames, SeRoute } from '@assets/val/route-constants';

@Component({
  selector: 'se-ais-callback',
  templateUrl: './se-ais-callback.component.html',
  styleUrls: ['./se-ais-callback.component.scss'],
})
export class SeAisCallbackComponent extends RouteComponent implements OnInit {  
  constructor(
    protected stateService: StateService,
    protected headerService: HeaderService,
    protected router: Router,
    protected route: ActivatedRoute,
    private loggingService: LoggingService,
    private queryParamsService: QueryParamsService,
    private aisAccountService: AisAccountService
  ) {
    super(router, route, stateService, headerService);
  }
  async ngOnInit(): Promise<void> {
    var params = this.queryParamsService.getQueryParams();

    var checkoutToken = params.checkoutToken;
    this.stateService.setToken(checkoutToken);

    // SeRoute.SELECT_AIS_ACCOUNT actually returns the /se/... route, to match we need the keys. Here selectAisAccountRouteKey = 'SELECT_AIS_ACCOUNT'
    const selectAisAccountRouteKey = Object.entries(SeRoute).find(([key, val]) => val === SeRoute.SELECT_AIS_ACCOUNT)?.[0];

    // In case two tabs from the AIS provider are active (Tink can spawn an extra one depending on the bank), one will be hidden on the redirection. 
    // To avoid duplicate navigation and report registration calls (even though the report fetching is idempotent), only trigger the logic if/when the user opens back the tab.
    const controlParams: StateControlParameters = {
      currentPageName: selectAisAccountRouteKey,
      token: this.stateService.getToken(),
    };
    const fetchedState: CheckoutState = await this.stateService.fetchStateNoUpdate(controlParams);

    // If the user has already navigated through another redirection tab, navigate directly there. 
    // Note that there is a big chance that it would already be the confirmation page at this point.
    if (fetchedState.currentPageInformation.pageName === selectAisAccountRouteKey) {
      this.fetchReport();
    } else {
      const route = this.stateService.getRouteFromState(fetchedState);
      await this.router.navigate([route], { queryParams: { id: this.stateService.getToken() } });
    }
  }

  async fetchReport(): Promise<void> {
    var params = this.queryParamsService.getQueryParams();

    var checkoutToken = params.checkoutToken;
    this.stateService.setToken(checkoutToken);

    var originProvider = params.originProvider;

    try {
      let callbackResponse: AccountCheckReportResponse;
      if (originProvider === AisProvider.TINK) {
        callbackResponse = await this.getTinkReport(params);
      } else if (originProvider === AisProvider.FINSHARK) {
        callbackResponse = await this.getFinsharkReport(params);
      } else {
        this.loggingService.error("Couldn't load report from ais account fetching! Return parameters: " + JSON.stringify(params));
      }

      if (callbackResponse && callbackResponse.status === AccountReportStatus.USER_CANCELLED) {
        this.loggingService.warn("User cancelled AIS flow. Returning to bank selector...");
        return this.handleUserCancel();
      }
      if (callbackResponse && callbackResponse.status !== AccountReportStatus.SUCCESS) {
        this.loggingService.error("Couldn't load callback from " + originProvider + " AIS callback. Status: " + callbackResponse.status + ", error: " + callbackResponse.providerErrorMessage);
      }

      await this.navigateResetParams();
    } catch (error) {
      this.loggingService.error("Internal error during ais callback. " + error);
      await this.navigateResetParams();
    }
  }

  async getTinkReport(params: QueryParams): Promise<AccountCheckReportResponse> {
    const payloadGetTinkReport: PayloadGetTinkReport = {
      state: params.state,
      verificationReportId: params.account_verification_report_id,
      error: params.error,
      errorDisplayMessage: params.error_display_message,
      errorReason: params.error_reason,
      errorType: params.error_type,
      message: params.message,
      providerName: params.provider_name,
      trackingId: params.tracking_id,
      resumeToken: params.resumeToken
    };

    return await this.aisAccountService.getTinkAccountReport(payloadGetTinkReport);
  }

  async getFinsharkReport(params: QueryParams): Promise<AccountCheckReportResponse> {
    const payloadGetFinsharkReport: PayloadGetFinsharkReport = {
      verifiedAccountId: params.verifiedAccountId,
      authorizationId: params.authorizationId,
      sessionId: params.sessionId,
      result: params.result,
      message: params.message,
      requestContext: params.requestContext,
      resumeToken: params.resumeToken
    };
    return await this.aisAccountService.getFinsharkAccountReport(payloadGetFinsharkReport);
  }

  /**
   * On user cancel we just go back to the bank selector. 
   * Since we have not left that step in the backend flow (just being redirected to the ais provider), we can just navigate in the frontend.
   */
  async handleUserCancel() {
    await this.router.navigate([SeRoute.SELECT_AIS_ACCOUNT], { queryParams: { id: this.stateService.getToken() } });
  }

  async navigateResetParams(): Promise<void> {
    const controlParams: StateControlParameters = {
      currentPageName: 'SELECT_AIS_ACCOUNT',
      token: this.stateService.getToken(),
    };

    const updatedState = await this.stateService.navigate(controlParams);
    const route = this.stateService.getRouteFromState(updatedState);

    // From the redirect we don't need to get all the Tink parameter passed along
    this.router.navigate([route], { queryParams: { id: this.stateService.getToken() } });
  }
}
